import {
  ArrowRight,
  Gift,
  PackageCheck
} from "lucide-react";

import {
  Link
} from "react-router";

import {
  useQuery
} from "@tanstack/react-query";

import {
  getGiftHamperSection,
  getPublicGiftHamperImageUrl
} from "../../services/catalog";

function isExternalUrl(
  value: string
) {
  return (
    value.startsWith("http://") ||
    value.startsWith("https://")
  );
}

export function CmsGiftHamperSection() {
  const query =
    useQuery({
      queryKey: [
        "homepage",
        "gift-hamper"
      ],

      queryFn:
        getGiftHamperSection
    });

  const section =
    query.data;

  if (
    query.isLoading
  ) {
    return null;
  }

  if (
    !section ||
    !section.isActive
  ) {
    return null;
  }

  const desktopImage =
    section.desktopImageKey
      ? getPublicGiftHamperImageUrl(
          section.id,
          "desktop",
          section.updatedAt
        )
      : null;

  const mobileImage =
    section.mobileImageKey
      ? getPublicGiftHamperImageUrl(
          section.id,
          "mobile",
          section.updatedAt
        )
      : desktopImage;

  const primaryUrl =
    section.primaryCtaUrl;

  const secondaryUrl =
    section.secondaryCtaUrl;

  return (
    <section className="rini-section bg-[#fffdf8]">
      <div className="rini-container">
        <div className="overflow-hidden rounded-[3rem] bg-[#eee0cf]">
          <div className="grid gap-10 p-8 sm:p-12 lg:grid-cols-[1.05fr_0.95fr] lg:items-center lg:p-16">
            <div>
              <span className="flex h-14 w-14 items-center justify-center rounded-full bg-white text-[#5e3b2c]">
                <Gift size={25} />
              </span>

              {section.eyebrow && (
                <p className="mt-8 text-sm font-semibold uppercase tracking-[0.22em] text-[#70845b]">
                  {section.eyebrow}
                </p>
              )}

              <h2 className="rini-heading mt-4 text-4xl font-semibold sm:text-5xl">
                {section.title}
              </h2>

              {section.description && (
                <p className="mt-6 max-w-xl text-lg leading-8 text-[#746d65]">
                  {section.description}
                </p>
              )}

              <div className="mt-8 flex flex-wrap gap-4">
                {section.primaryCtaText &&
                  primaryUrl &&
                  (
                    isExternalUrl(
                      primaryUrl
                    ) ? (
                      <a
                        href={
                          primaryUrl
                        }
                        target="_blank"
                        rel="noreferrer"
                        className="rini-button-primary"
                      >
                        {section.primaryCtaText}
                        <ArrowRight size={18} />
                      </a>
                    ) : (
                      <Link
                        to={
                          primaryUrl
                        }
                        className="rini-button-primary"
                      >
                        {section.primaryCtaText}
                        <ArrowRight size={18} />
                      </Link>
                    )
                  )}

                {section.secondaryCtaText &&
                  secondaryUrl &&
                  (
                    isExternalUrl(
                      secondaryUrl
                    ) ? (
                      <a
                        href={
                          secondaryUrl
                        }
                        target="_blank"
                        rel="noreferrer"
                        className="rini-button-secondary"
                      >
                        {section.secondaryCtaText}
                      </a>
                    ) : (
                      <Link
                        to={
                          secondaryUrl
                        }
                        className="rini-button-secondary"
                      >
                        {section.secondaryCtaText}
                      </Link>
                    )
                  )}
              </div>
            </div>

            <div className="relative min-h-80 overflow-hidden rounded-[2rem] bg-gradient-to-br from-[#c49a70] to-[#6f5140]">
              {desktopImage ? (
                <picture>
                  {mobileImage && (
                    <source
                      media="(max-width: 767px)"
                      srcSet={
                        mobileImage
                      }
                    />
                  )}

                  <img
                    src={
                      desktopImage
                    }
                    alt="RiNi Gift Hampers"
                    className="absolute inset-0 h-full w-full object-cover"
                  />
                </picture>
              ) : (
                <div className="flex min-h-80 items-center justify-center text-center text-white">
                  <div>
                    <PackageCheck
                      size={52}
                      className="mx-auto"
                      strokeWidth={1.3}
                    />

                    <p className="mt-5 font-serif text-3xl">
                      RiNi Gift Collection
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
